/*
 * Copyright 2025 Intuition Robotics ltd
 */
import * as React from "react";
import {useState, useContext} from "react";
import {DialogTitle} from "../../../utils/DialogTitle";
import {PADDING_LEFT, PADDING_RIGHT} from "../../../utils/AppDialog";
import {VGap} from "../../widget/VGap";
import {HGap} from "../../widget/HGap";
import {DB_Patient} from "@app/app-shared/patient";
import {PatientSearch} from "../../widget/PatientSearch";
import {PatientHandle} from "./PatientHandle";
import {PatientSelectionButtons} from "./PatientSelectionButtons";
import {FONTS} from "@res/fonts";
import * as emotion from "emotion";
import {DialogModule, ToastModule} from "@intuitionrobotics/thunderstorm/frontend";
import {GroupType} from "@app/ir-q-app-common/types/contacts";
import {RadioButton} from "../../widget/RadioButton";
import {LoadingBar} from "../../widget/LoadingBar";
import {ChatBoxInput} from "./ChatBoxInput";
import DragAndDrop from "../../widget/DragAndDrop";
import {ICONS, icon_schedule_src} from "@res/icons";
import {COLORS} from "@res/colors";
import {ChatsContext} from "./ChatsContext";
import {PatientsContext} from "../patients/PatientsContext";
import {ScheduleDateTimePicker} from "./ScheduleDateTimePicker";

type Props = {
    patients: DB_Patient[]
    selectedPatients: string[]
    onSelectionChange: (selectedPatients: string[]) => void
    onCancel: () => void
    onSend: (dataToSend: { text?: string, files?: File[] }, selectedPatients: string[], needResponse: boolean, thumbnailFile?: File, scheduledSend?: number) => void
    onChooseFromList: () => void
    onSelectGroupType?: (groupType: GroupType) => void
    preselectedPatient?: DB_Patient
    selectedGroupType?: GroupType | undefined
    needsGroupSelection: boolean
    availableGroupTypes: Set<GroupType>
}

// const color = "#303030";

const progressBarWrapper = emotion.css(
    {
        position: "absolute",
        top: 0,
        left: 0,
        width: "100%"
    }
);

export const ComposeMessagePanel = (props: Props) => {
    const [requireResponse, setRequireResponse] = useState<boolean>(false)
    const [isSending, setIsSending] = useState<boolean>(false)
    const [thumbnailFile, setThumbnailFile] = useState<File | undefined>()
    const [isUploadCustomThumbnmail, setIsUploadingCustomThumbnail] = useState<boolean>(false)
    const [showScheduler, setShowScheduler] = useState<boolean>(false)
    const [scheduledSend, setScheduledSend] = useState<number | undefined>(undefined)
    const {demo} = useContext(PatientsContext)

    const [dataToSend, setDataToSend] = useState<{
        message?: string
        audioFile?: File
        files?: File[]
    }>({})

    const cCtx = useContext(ChatsContext);

    const preselectedPatient = props.preselectedPatient;

    const renderLetterCount = () => {
        const message = dataToSend.message;
        const letters = message ? message.length : 0;
        return <div style={{fontSize: 14, color: "#6a6b6d", fontWeight: "normal", paddingLeft: 10}}>
            {FONTS.reg_14_secondary_dark(`${letters}/140 characters`)}
        </div>;
    };

    const renderUploadCustomThumbnail = () => {
        if (!thumbnailFile)
            return <div style={{marginTop: 10, marginLeft: 25}}>
                <p style={{marginBottom: 10}}>Upload here custom thumbnail</p>
                <DragAndDrop height={"100px"} validate={["png",
                    "jpg",
                    "jpeg"]}
                             onChange={(files) => {
                                 if (files.accepted.length > 1)
                                     return ToastModule.toastError(
                                         'Please upload only one file');

                                 setThumbnailFile(files.accepted[0])
                             }}
                />
            </div>

        return <div style={{margin: 25}}>
            <p style={{marginBottom: 10}}>Thumbnail: </p>
            <div style={{display: "flex"}}>
                <div className={"clickable"} onClick={() => setThumbnailFile(undefined)}>
                    {ICONS.close(COLORS.red, 20)}
                </div>
                {FONTS.medium_12(thumbnailFile.name, {marginLeft: 10, color: COLORS.primary_dark_blue, alignSelf: "center"})}
            </div>
        </div>
    };

    const send = () => {
        const {message, audioFile, files} = dataToSend;
        if (!message && !audioFile && (!files || files.length === 0))
            return;

        if (isSending)
            return;

        setIsSending(true)

        if (demo) {
            DialogModule.close();
            return;
        }

        const recipients = props.selectedPatients;
        if (audioFile)
            props.onSend({files: [audioFile]}, recipients, requireResponse, undefined, scheduledSend);
        else if (files && files.length > 0)
            props.onSend({text: message?.trim(), files: files}, recipients, requireResponse, thumbnailFile, scheduledSend)
        else if (message)
            props.onSend({text: message.trim()}, recipients, requireResponse, undefined, scheduledSend);
    };

    const isOkEnabled = (): boolean => {
        if (isSending)
            return false;

        const {message, audioFile, files} = dataToSend;

        if ((!message || message.trim().length === 0) && (!audioFile) && (!files || files?.length === 0))
            return false;

        if (props.needsGroupSelection && !props.selectedGroupType)
            return false;

        if (!cCtx.alreadyInitialized && props.patients.length !== 1)
            return false;

        return props.selectedPatients.length > 0;
    };

    const renderSelectedPatients = () => {
        return <div style={{display: "flex", flexFlow: "row wrap", maxHeight: 50, overflow: "auto"}}>
            {props.selectedPatients.map(renderSelectedPatient)}
        </div>;
    };

    const renderSelectedPatient = (id: string) => {
        const patient = props.patients?.find(it => it._id === id);
        if (!patient)
            return "";
        return <PatientHandle key={patient._id} patient={patient} onDelete={() => removePatientFromSelection(patient._id)}/>;
    };

    const removePatientFromSelection = (id: string) => {
        const selectedPatients = props.selectedPatients.filter(it => it !== id);
        props.onSelectionChange(selectedPatients);
    };

    // Only dealing with CARE_TEAM and CLINIC and External groups
    const renderGroupsSelection = () => {
        return (<div className="ll_h_c" style={{marginTop: 20, paddingLeft: PADDING_LEFT, paddingRight: PADDING_RIGHT}}>
            From:
            {props.availableGroupTypes.has(GroupType.CUSTOMER_SERVICE) && <div style={{paddingLeft: 20, paddingRight: 25}}>
                <RadioButton on={props.selectedGroupType === GroupType.CUSTOMER_SERVICE} onChange={(newState) => onSelectGroupType(GroupType.CUSTOMER_SERVICE)}/> Customer
                Service
            </div>}
            {props.availableGroupTypes.has(GroupType.CLINIC) && <div style={{paddingRight: 25}}>
                <RadioButton on={props.selectedGroupType === GroupType.CLINIC} onChange={(newState) => onSelectGroupType(GroupType.CLINIC)}/> {GroupType.CLINIC}
            </div>}
            {props.availableGroupTypes.has(GroupType.EXTERNAL) && <div>
                <RadioButton on={props.selectedGroupType === GroupType.EXTERNAL} onChange={(newState) => onSelectGroupType(GroupType.EXTERNAL)}/> {GroupType.EXTERNAL}
            </div>}
        </div>);
    }

    const onSelectGroupType = (groupType: GroupType) => {
        props.onSelectGroupType?.(groupType);
        props.onSelectionChange([]);
    }

    const renderPatientSelectionArea = () => {

        if (props.needsGroupSelection && !props.selectedGroupType)
            return null

        return <div style={{paddingLeft: PADDING_LEFT, paddingRight: PADDING_RIGHT}}>
            {/*{renderTitle("Choose patients")}*/}
            <div className={"ll_h_c"}>
                To:
                <HGap h={20}/>
                {/*<Checkbox on={all} onChange={all => {*/}
                {/*	props.onSelectionChange([]);*/}
                {/*	setState({all});*/}
                {/*}}/>*/}
                {/*<HGap h={10}/>*/}
                {/*<div style={{color, fontSize: 16, fontWeight: all ? "bold" : "normal"}}>All</div>*/}
                {/*<HGap h={20}/>*/}
                <PatientSearch
                    patients={props.patients}
                    selectedPatients={props.selectedPatients}
                    onSelected={selectedPatients => {
                        props.onSelectionChange(selectedPatients);
                    }}
                />
                <div style={{flex: 1}}/>
                <HGap h={10}/>
                <div className={isSending ? "cursor_disabled" : "clickable"}
                     onClick={() => {
                         if (isSending)
                             return;
                         props.onChooseFromList();
                     }}>
                    {FONTS.reg_16_secondary_dark("Advanced search", {textDecoration: "underline"})}
                </div>
            </div>
            <VGap v={16}/>
            {renderSelectedPatients()}
        </div>;
    };

    const renderMessageTextArea = () => {
        return <ChatBoxInput
            onRequireResponse={(_requireResponse: boolean) => setRequireResponse(_requireResponse)}
            isUploadingCustomThumbnail={() => setIsUploadingCustomThumbnail(true)}
            onChange={(_dataToSend: { text?: string, audioFile?: File, files?: File[] }) => {
                setDataToSend({
                    audioFile: _dataToSend.audioFile,
                    message: _dataToSend.text,
                    files: _dataToSend.files
                })
                setIsUploadingCustomThumbnail(!!_dataToSend.files?.some(f => f.name.endsWith(".mp4")))
            }}/>
    };

    return <div className={"ll_v_s fill"} style={{width: 600, borderRadius: 4, background: "white"}}>
        <DialogTitle title={`Compose new message`}/>
        <div className={"ll_v_s"}
             style={{
                 flex: 1,
                 position: "relative"
             }}>
            <VGap v={5}/>
            {props.needsGroupSelection && renderGroupsSelection()}
            <VGap v={15}/>
            <div className={`${progressBarWrapper}`}>
                {
                    isSending && <LoadingBar status={"loading"}/>
                }
            </div>
            {!preselectedPatient && renderPatientSelectionArea()}
            <VGap v={5}/>
            {renderMessageTextArea()}
            <VGap v={8}/>
            {renderLetterCount()}
            <VGap v={8}/>
            {isUploadCustomThumbnmail && renderUploadCustomThumbnail()}
        </div>
        {showScheduler && <>
            <div
                style={{position: "fixed", inset: 0, zIndex: 101}}
                onClick={() => setShowScheduler(false)}
            />
            <div style={{
                position: "fixed",
                zIndex: 102,
                top: "50%",
                left: "calc(50% + 310px)",
                transform: "translateY(-50%)",
                backgroundColor: "white",
                boxShadow: "0 4px 20px rgba(0,0,0,0.2)",
                borderRadius: 8
            }}>
                <ScheduleDateTimePicker
                    onSchedule={(timestamp) => {
                        setScheduledSend(timestamp);
                        if (timestamp)
                            setShowScheduler(false);
                    }}
                />
            </div>
        </>}
        <VGap v={27}/>
        <PatientSelectionButtons
            hideNumberOfPatients={!!props.preselectedPatient}
            patientCount={props.patients?.length}
            selectedCount={props.selectedPatients.length}
            okEnabled={isOkEnabled()}
            okText={scheduledSend ? "Schedule message" : "Send message"}
            onCancel={props.onCancel}
            onOK={() => send()}
            cancelDisabled={isSending}
            extraButton={isOkEnabled()
                ? <div
                    className={"clickable"}
                    title={scheduledSend ? `Scheduled: ${new Date(scheduledSend).toLocaleString()}` : "Schedule send"}
                    onClick={(e) => {
                        e.stopPropagation();
                        setShowScheduler(prev => !prev);
                        if (showScheduler)
                            setScheduledSend(undefined);
                    }}
                    style={{display: "flex", alignItems: "center", marginLeft: 8, opacity: scheduledSend ? 1 : 0.45}}>
                    <img src={icon_schedule_src} style={{width: 20, height: 20}}/>
                </div>
                : undefined
            }
        />
    </div>;

}
